import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Collapse,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp, Refresh } from "@mui/icons-material";
import { fetchProcessUsage } from "../utils/usageStatusApi";
import UsageCostChart from "./UsageCostChart";
import { INTERNAL_MESSAGE_COST_CENTS, INTERNAL_PRODUCT_COST_CENTS } from "../utils/internalUsageCost";

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatCount = (value, noun) => `${formatNumber(value)} ${noun}${value === 1 ? "" : "s"}`;
const formatDate = (value) => value ? new Date(value).toLocaleString() : "—";
const formatRangeDate = (value) => new Date(value).toLocaleString(undefined, {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});
const localDateTime = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const initialRange = () => {
  const to = new Date();
  return { from: localDateTime(new Date(to.getTime() - 24 * 60 * 60 * 1000)), to: localDateTime(to) };
};

const metricFields = [
  ["productUpdatesCount", "Product updates", formatNumber],
  ["chatMessagesCount", "Chat messages", formatNumber],
  ["visitorsCount", "Visitors", formatNumber],
  ["orderTrackingCount", "Order tracking", formatNumber],
];

const usageSortValue = (field) => (run) =>
  run.errorMessage || !run.usage ? null : Number(run.usage[field] || 0);

const columns = [
  { key: "tenant", label: "Tenant" },
  { key: "type", label: "Run / type" },
  { key: "fromDate", label: "Usage window" },
  { key: "productUpdatesCount", label: "Product updates", value: usageSortValue("productUpdatesCount"), numeric: true },
  { key: "chatMessagesCount", label: "Chat messages", value: usageSortValue("chatMessagesCount"), numeric: true },
];

const orderDescription = (direction) => direction === "desc" ? "high to low" : "low to high";

const UsageRun = ({ run }) => {
  const [expanded, setExpanded] = useState(false);
  const unavailable = Boolean(run.errorMessage) || !run.usage;
  const processes = run.processes || [];
  return (
    <Fragment>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" aria-expanded={expanded} aria-label={`${expanded ? "Hide" : "Show"} details for ${run.eventMainId}`} onClick={() => setExpanded(!expanded)}>
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>{run.tenantName || run.tenantId || "Unknown tenant"}</Typography>
          <Typography variant="caption" color="text.secondary">{run.tenantId}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{run.type || "—"}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{run.eventMainId}</Typography>
        </TableCell>
        <TableCell sx={{ minWidth: 190 }}>
          <Typography variant="body2">{formatDate(run.fromDate)}</Typography>
          <Typography variant="caption" color="text.secondary">to {formatDate(run.toDate)}</Typography>
        </TableCell>
        <TableCell align="right">{unavailable ? "—" : formatNumber(run.usage.productUpdatesCount)}</TableCell>
        <TableCell align="right">{unavailable ? "—" : formatNumber(run.usage.chatMessagesCount)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={columns.length + 1} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 3, backgroundColor: "#fffaf6" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Run recorded {formatDate(run.timestamp)}</Typography>
              {unavailable && <Alert severity="warning" sx={{ mb: 2 }}>{run.errorMessage || "Usage is unavailable for this run."}</Alert>}
              {!unavailable && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 2 }}>
                  {[
                    ["Standard messages", run.usage.standardMessagesCount],
                    ["Web search messages", run.usage.webSearchMessagesCount],
                    ["Reasoning messages", run.usage.reasoningMessagesCount],
                    ["Visitors", run.usage.visitorsCount],
                    ["Order tracking", run.usage.orderTrackingCount],
                  ].map(([label, value, format = formatNumber]) => (
                    <Box key={label}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography fontWeight={700}>{format(value)}</Typography></Box>
                  ))}
                </Box>
              )}
              <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>Processes ({processes.length})</Typography>
              {processes.length ? (
                <TableContainer>
                  <Table size="small" aria-label={`Processes for ${run.eventMainId}`}>
                    <TableHead><TableRow>{["Process", "Status", "Tasks", "Started", "Finished"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
                    <TableBody>{processes.map((process) => (
                      <TableRow key={process.id || process.name}>
                        <TableCell>{process.name}</TableCell><TableCell>{process.statusImport || "—"}</TableCell>
                        <TableCell>{formatNumber(process.totalTasks)}</TableCell><TableCell>{formatDate(process.startTime)}</TableCell>
                        <TableCell>{formatDate(process.finishTime)}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </TableContainer>
              ) : <Typography variant="body2" color="text.secondary">No processes reported.</Typography>}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

const UsageStatusTab = () => {
  const [range, setRange] = useState(initialRange);
  const [query, setQuery] = useState(() => ({ ...range }));
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadedRange, setLoadedRange] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState({ key: null, direction: "desc" });

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setRuns([]);
    setLoadedRange(null);
    fetchProcessUsage(query.from, query.to, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setRuns([...data].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
        setSelectedTenantId((current) => data.some((run) => (run.tenantId || "") === current) ? current : null);
        setLoadedRange(query);
        setPage(0);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err.response?.status === 403
          ? "Usage Status is available to System Admin and Customer Success accounts."
          : err.response?.data?.message || err.message || "Unable to load usage. Please try again.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [query]);

  const validation = !range.from || !range.to ? "Select both dates."
    : new Date(range.from) > new Date(range.to) ? "From date must be before To date."
    : new Date(range.from) > new Date() ? "From date cannot be in the future." : "";
  const tenantOptions = useMemo(() => {
    const tenants = new Map();
    runs.forEach((run) => {
      const id = run.tenantId || "";
      tenants.set(id, { id, name: run.tenantName || id || "Unknown tenant" });
    });
    return [...tenants.values()].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  }, [runs]);
  const filteredRuns = useMemo(() => {
    const term = search.trim().toLowerCase();
    return runs.filter((run) => (selectedTenantId === null || (run.tenantId || "") === selectedTenantId) &&
      [run.tenantName, run.tenantId, run.eventMainId, run.type].some((value) => String(value || "").toLowerCase().includes(term)));
  }, [runs, search, selectedTenantId]);
  const sortedRuns = useMemo(() => {
    const column = columns.find(({ key }) => key === sort.key);
    if (!column?.numeric) return filteredRuns;
    return [...filteredRuns].sort((a, b) => {
      const left = column.value(a);
      const right = column.value(b);
      const leftMissing = left == null || Number.isNaN(left);
      const rightMissing = right == null || Number.isNaN(right);
      // Keep unavailable values at the end for either direction.
      if (leftMissing || rightMissing) return Number(leftMissing) - Number(rightMissing);
      const comparison = left - right;
      return sort.direction === "desc" ? -comparison : comparison;
    });
  }, [filteredRuns, sort]);
  const sortColumn = columns.find(({ key }) => key === sort.key);
  const changeSort = (column) => {
    setSort((current) => {
      if (current.key !== column.key) return { key: column.key, direction: "desc" };
      if (current.direction === "desc") return { key: column.key, direction: "asc" };
      return { key: null, direction: "desc" };
    });
    setPage(0);
  };
  const totals = useMemo(() => filteredRuns.reduce((sum, run) => {
    if (run.usage && !run.errorMessage) {
      metricFields.forEach(([field]) => {
        const value = Number(run.usage[field] || 0);
        sum[field] = (sum[field] || 0) + value;
      });
    }
    return sum;
  }, {}), [filteredRuns]);
  const unavailableCount = filteredRuns.filter((run) => run.errorMessage || !run.usage).length;

  return (
    <Paper elevation={0} sx={{ mx: { xs: 1, md: 2 }, p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #ffe4d2", background: "linear-gradient(180deg, #fffaf6, #fff 200px)" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(220px, 1fr) auto" }, alignItems: "start", gap: 2, mb: 2 }}>
        <Box>
          <Typography component="h2" variant="h6" fontWeight={800}>Usage Status</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>Usage and costs for processing runs across all tenants.</Typography>
        </Box>
        <Box>
          <Box component="form" onSubmit={(event) => { event.preventDefault(); if (!validation && !loading) setQuery({ ...range }); }}
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr)) auto" }, alignItems: "start", gap: 1.5 }}>
            <TextField label="From date" type="datetime-local" size="small" value={range.from} disabled={loading}
              onChange={(event) => setRange({ ...range, from: event.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField label="To date" type="datetime-local" size="small" value={range.to} disabled={loading}
              onChange={(event) => setRange({ ...range, to: event.target.value })} InputLabelProps={{ shrink: true }} />
            <Button type="submit" variant="contained" disabled={loading || Boolean(validation)} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
              sx={{ minHeight: 40, whiteSpace: "nowrap", backgroundColor: "#f97316", textTransform: "none", borderRadius: 2, "&:hover": { backgroundColor: "#ea580c" } }}>
              {loading ? "Loading…" : "Load usage"}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Local time · Default range: last 1 day</Typography>
        </Box>
      </Box>
      {validation && <Alert severity="warning" sx={{ mt: 2 }}>{validation}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {loading && <Box role="status" sx={{ py: 6, textAlign: "center" }}><CircularProgress sx={{ color: "#f97316" }} /><Typography sx={{ mt: 2 }}>Loading processing usage…</Typography></Box>}
      {!loading && loadedRange && <>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
          <Autocomplete
            size="small"
            options={tenantOptions}
            value={tenantOptions.find((tenant) => tenant.id === selectedTenantId) || null}
            getOptionLabel={(tenant) => tenant.id && tenant.name !== tenant.id ? `${tenant.name} (${tenant.id})` : tenant.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, tenant) => { setSelectedTenantId(tenant ? tenant.id : null); setPage(0); }}
            renderInput={(params) => <TextField {...params} label="Tenant" placeholder="All tenants" />}
            noOptionsText="No tenants in this date range"
            sx={{ width: { xs: "100%", sm: 260 } }}
          />
          <TextField label="Search tenant, run or type" size="small" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} sx={{ flex: { xs: "1 1 100%", sm: "1 1 220px", lg: "0 1 240px" }, minWidth: 0 }} />
          <Box sx={{ ml: { lg: "auto" }, textAlign: { xs: "left", lg: "right" }, flexBasis: { xs: "100%", lg: "auto" } }}>
            <Typography variant="body2" color="text.secondary">{formatCount(filteredRuns.length, "run")} · {formatCount(new Set(filteredRuns.map((run) => run.tenantId).filter(Boolean)).size, "tenant")}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Runs: {formatRangeDate(loadedRange.from)} – {formatRangeDate(loadedRange.to)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.5, mb: 1 }}>
          {metricFields.map(([field, label, format]) => (
            <Paper key={field} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: "#f3dfd1" }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, color: "#202531", mt: 0.5 }}>{filteredRuns.length > 0 && unavailableCount === filteredRuns.length ? "—" : format(totals[field])}</Typography>
            </Paper>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            preezie internal cost (USD): {INTERNAL_MESSAGE_COST_CENTS} cents per message · {INTERNAL_PRODUCT_COST_CENTS} cents per product.
          </Typography>
          {filteredRuns.length > 0 && <Typography variant="caption" color="text.secondary">
            {sortColumn ? `Ordered by ${sortColumn.label}: ${orderDescription(sort.direction)}.` : "Click Product updates or Chat messages to sort."}
          </Typography>}
        </Box>
        {unavailableCount > 0 && <Alert severity="warning" sx={{ mb: 2 }}>Usage is unavailable for {formatCount(unavailableCount, "run")}. Unavailable usage is excluded from totals; expand a run for details.</Alert>}
        {filteredRuns.length === 0 ? <Alert severity="info">{runs.length ? "No runs match your filters." : "No processing runs found in this date range."}</Alert> : <>
          <TableContainer sx={{ border: "1px solid #eee", borderRadius: 3 }}>
            <Table size="small" aria-label="Processing usage by run">
              <TableHead sx={{ backgroundColor: "#fff7ed" }}><TableRow>
                <TableCell aria-label="Details" />
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.numeric ? "right" : "left"}
                    sortDirection={sort.key === column.key ? sort.direction : false} sx={{ fontWeight: 700 }}>
                    {column.numeric ? <TableSortLabel active={sort.key === column.key}
                      direction={sort.key === column.key ? sort.direction : "desc"}
                      aria-label={`Order by ${column.label}`} onClick={() => changeSort(column)}>
                      {column.label}
                    </TableSortLabel> : column.label}
                  </TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>{sortedRuns.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((run) => <UsageRun key={run.eventMainId} run={run} />)}</TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredRuns.length} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 15, 25, 50]}
            onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} />
        </>}
        <Box sx={{ mt: 2 }}>
          <UsageCostChart runs={filteredRuns} fromDate={loadedRange.from} toDate={loadedRange.to} tenantLimit={rowsPerPage} />
        </Box>
      </>}
    </Paper>
  );
};

export default UsageStatusTab;
